import { Flex, Tab, TabList, TabPanel, TabPanels, Tabs } from '@chakra-ui/react';
import { CHATBOT_WIDGET_ICON_HEIGHT, NAVBAR_HEIGHT } from 'constants/editor';
import { useAppDispatch, useAppSelector } from 'modules/Editor/store';
import { selectEditBackgroundView } from 'modules/Editor/store/editorSettingsSelector';
import { setEditBackgroundView } from 'modules/Editor/store/editorSettingsSlice';
import { selectHasActiveWidgets } from 'modules/Editor/store/widgetSelector';
import { SidePanel, SidePanelHeader } from 'modules/common/components/SidePanel';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ColorMenuTab } from './ColorMenu/ColorMenuTab';
import { PatternMenuTab } from './PatternMenu/PatternMenuTab';

const COLOR_TAB_INDEX = 0;
const PATTERN_TAB_INDEX = 1;

export const EditBackgroundMenu = () => {
  const editBackgroundView = useAppSelector(selectEditBackgroundView);
  const isOpen = useMemo(() => editBackgroundView !== undefined, [editBackgroundView]);

  const { t } = useTranslation('editor_edit_background_menu', { useSuspense: false });
  const dispatch = useAppDispatch();

  const closeMenu = useCallback(() => dispatch(setEditBackgroundView(undefined)), [dispatch]);
  const hasActiveWidgets = useAppSelector(selectHasActiveWidgets);

  // To detect, whether when selecting a widget and toolbar changed
  // they have background edit side menu open, if yes, close.
  useEffect(() => {
    if (hasActiveWidgets && isOpen) {
      dispatch(setEditBackgroundView(undefined));
    }
  }, [hasActiveWidgets, isOpen, dispatch]);

  // Can't focus on root container and focusing tab tablist cause content to be disappear,
  // Set focus on close button instead
  const headerCloseButtonRef = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    if (isOpen && headerCloseButtonRef?.current && editBackgroundView !== undefined) {
      headerCloseButtonRef.current.focus();
    }
  }, [isOpen, editBackgroundView, headerCloseButtonRef]);

  const handleClickColorTab = () => {
    dispatch(setEditBackgroundView('color'));
  };

  const handleClickPatternTab = () => {
    dispatch(setEditBackgroundView('pattern'));
  };

  if (!isOpen) return null;

  return (
    <SidePanel isOpen={isOpen} placement={'right'} testId={'edit-background-menu'} w={'sm'}>
      <SidePanelHeader
        title={t('editBackground')}
        onClose={closeMenu}
        closeButtonAriaLabel={'Close edit background menu'}
        ref={headerCloseButtonRef}
      />
      <Tabs
        h={'full'}
        overflow={'auto'}
        isLazy
        index={editBackgroundView === 'pattern' ? PATTERN_TAB_INDEX : COLOR_TAB_INDEX}
      >
        <TabList h={12}>
          <Tab
            _focus={{ boxShadow: 'inset var(--vg-shadows-outline)' }}
            fontSize='sm'
            fontWeight='semibold'
            textTransform={'capitalize'}
            tabIndex={COLOR_TAB_INDEX}
            onClick={handleClickColorTab}
          >
            {t('color')}
          </Tab>
          <Tab
            _focus={{ boxShadow: 'inset var(--vg-shadows-outline)' }}
            data-testid='tab-pattern'
            fontSize='sm'
            fontWeight='semibold'
            textTransform={'capitalize'}
            tabIndex={PATTERN_TAB_INDEX}
            onClick={handleClickPatternTab}
          >
            {t('pattern')}
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel
            height={'full'}
            overflow='auto'
            p={0}
            paddingBottom={`${CHATBOT_WIDGET_ICON_HEIGHT + NAVBAR_HEIGHT}px`}
          >
            <Flex direction='column' height={'full'} gap={4}>
              <ColorMenuTab />
            </Flex>
          </TabPanel>
          <TabPanel height={'full'} overflow='auto' paddingBottom={`${CHATBOT_WIDGET_ICON_HEIGHT + NAVBAR_HEIGHT}px`}>
            <Flex direction='column' height={'full'} gap={4}>
              <PatternMenuTab />
            </Flex>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </SidePanel>
  );
};
